import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import FilterDropdown from '../../../components/custom-input/FilterDropdown';
import DateRangeInput from '../../../components/custom-input/DateInputRange';
import { Loader } from '../../../components';
import Modal from '../../../components/modal/Modal';
import ReportSummaryCard from '../../../components/report-summary-card/ReportSummaryCard';
import PagedTable, { type PagedTableHeader } from '../../../components/sorted-table/PagedTable';
import AccountSearch from '../../../components/custom-input/AccountSearch';
import AnimalSearch from '../../../components/custom-input/AnimalSearch';
import ValidatedInput from '../../../components/custom-input/ValidatedInput';
import type { FilterOption } from '../../../components/custom-input/FilterDropdown';
import { AlertSVG, ClearFilterSVG, DownloadSVG, FilterSVG } from '../../../components/svgs';
import {
  downloadCommissionReport,
  getCommissionReportSummary,
  getMarketplacePaymentTransactionOData,
} from '../../../api/marketPlacePaymentTransactionOrderDetailApi';
import { showToast } from '../../../services/toast.service';
import { toastMessages } from '../../../constants/errorMessages';
import {
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  COMMISSION_REPORT_CONSTANTS,
  LABEL_CONSTANTS,
  MARKETPLACE_MY_ORDERS_LABELS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
  VALIDATION_ALERT_CONSTANTS,
} from '../../../constants/common';
import { StandardParamsToODataParams, type StandardParams } from '../../../types/interfaces/apiParams.interfaces';
import type { Account } from '../../../types/interfaces/account.interfaces';
import type { Animal } from '../../../types/interfaces/animal.interfaces';
import type { MarketPlacePaymentTransactionOrderDetail } from '../../../types/interfaces/marketPlace.interfaces';
import { dateFormatMMDDYYYY, formatDateToShortMonthDayYear } from '../../../utils/commonUtils';
import { dayAfterDate } from '../../../utils/dateHandler';
import './commissionReport.scss';

interface CommissionReportRow {
  id: string;
  orderId: string;
  orderDate: string | undefined;
  buyer: string;
  sellerBreeder: string;
  productAnimal: string;
  orderAmount: number;
  commPercent: number;
  commission: number;
  status: string;
}

const COMMISSION_PAGED_HEADERS: PagedTableHeader[] = [
  { displayName: TABLE_HEADER_CONSTANTS.ORDER_ID },
  { displayName: TABLE_HEADER_CONSTANTS.ORDER_DATE, sortValue: 'createdDatetime' },
  { displayName: TABLE_HEADER_CONSTANTS.BUYER },
  { displayName: TABLE_HEADER_CONSTANTS.SELLER },
  { displayName: TABLE_HEADER_CONSTANTS.ANIMAL },
  { displayName: TABLE_HEADER_CONSTANTS.ORDER_AMOUNT, sortValue: 'sellerAmountCents' },
  { displayName: TABLE_HEADER_CONSTANTS.COMM_PERCENT, sortValue: 'rsgCommissionCents' },
  { displayName: TABLE_HEADER_CONSTANTS.COMMISSION, sortValue: 'rsgCommissionCents' },
  { displayName: TABLE_HEADER_CONSTANTS.STATUS },
];

const COMMISSION_REPORT_ODATA_INCLUDE = 'InventoryTransaction.Specimen.Animal, SellerAccount';
const CREATED_DATETIME_FILTER_FIELD = 'createdDatetime';
const COMMISSION_REPORT_ODATA_DEFAULT_SORT = 'createdDatetime^';
const buildCreatedDatetimeRangeODataFilter = (startDate: Date, endDate: Date): string => {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const endExclusive = dayAfterDate(new Date(endDate));
  endExclusive.setHours(0, 0, 0, 0);
  return `(${CREATED_DATETIME_FILTER_FIELD} ge ${start.toISOString()} and ${CREATED_DATETIME_FILTER_FIELD} lt ${endExclusive.toISOString()})`;
};

const getCommissionReportODataDateFilter = (state: CommissionReportFilterState): string | undefined => {
  const { startDate, endDate } = state.orderDateRange;
  if (!startDate || !endDate) return undefined;
  return buildCreatedDatetimeRangeODataFilter(startDate, endDate);
};

const { ANIMAL_SEARCH, BUYER_SEARCH, SELLER_SEARCH } = COMMISSION_REPORT_CONSTANTS;

type CommissionEntitySearchValue = {
  kind: typeof BUYER_SEARCH | typeof SELLER_SEARCH | typeof ANIMAL_SEARCH;
};

const COMMISSION_ENTITY_SEARCH_PLACEHOLDER: FilterOption<CommissionEntitySearchValue> = {
  name: '',
  value: { kind: BUYER_SEARCH },
};

const COMMISSION_ENTITY_SEARCH_OPTIONS: FilterOption<CommissionEntitySearchValue>[] = [
  { name: ANIMAL_CONSTANTS.ANIMAL, value: { kind: ANIMAL_SEARCH } },
  { name: TABLE_HEADER_CONSTANTS.BUYER, value: { kind: BUYER_SEARCH } },
  { name: TABLE_HEADER_CONSTANTS.SELLER, value: { kind: SELLER_SEARCH } },
];

const buildEntitySearchODataFilter = (state: CommissionReportFilterState): string | undefined => {
  const { entitySearchCriteria, entitySearchAccount, entitySearchAnimal } = state;
  if (!entitySearchCriteria?.name) return undefined;
  const { kind } = entitySearchCriteria.value;
  if (kind === BUYER_SEARCH && entitySearchAccount?.accountId != null) {
    return `inventoryTransaction/accountId eq ${entitySearchAccount.accountId}`;
  }
  if (kind === SELLER_SEARCH && entitySearchAccount?.accountId != null) {
    return `AccountId eq ${entitySearchAccount.accountId}`;
  }
  if (kind === ANIMAL_SEARCH && entitySearchAnimal?.animalId != null) {
    return `inventoryTransaction/specimen/animalId eq ${entitySearchAnimal.animalId}`;
  }
  return undefined;
};

const getCommissionReportODataFilter = (state: CommissionReportFilterState): string | undefined => {
  const datePart = getCommissionReportODataDateFilter(state);
  const entityPart = buildEntitySearchODataFilter(state);
  const parts = [datePart, entityPart].filter((filterPart): filterPart is string => Boolean(filterPart));
  if (parts.length === 0) return undefined;
  return parts.join(' and ');
};

const COMMISSION_MOBILE_PAGE_SIZE = 10;

export interface CommissionReportFilterState {
  orderDateRange: { startDate: Date | null; endDate: Date | null };
  entitySearchCriteria: FilterOption<CommissionEntitySearchValue>;
  entitySearchAccount: Account | undefined;
  entitySearchAnimal: Animal | undefined;
}

const getDefaultCommissionFilters = (): CommissionReportFilterState => ({
  orderDateRange: { startDate: null, endDate: null },
  entitySearchCriteria: COMMISSION_ENTITY_SEARCH_PLACEHOLDER,
  entitySearchAccount: undefined,
  entitySearchAnimal: undefined,
});

const mapApiRowToCommissionReportRow = (item: MarketPlacePaymentTransactionOrderDetail): CommissionReportRow => {
  const createdRawDate =
    (item as MarketPlacePaymentTransactionOrderDetail & { createdDatetime?: string }).createdDatetime ?? item.createdDateTime;
  const orderDate = createdRawDate ? new Date(createdRawDate).toLocaleDateString() : '';

  const sellerCents = item.sellerAmountCents ?? 0;
  const rsgCents = item.rsgCommissionCents ?? 0;
  const grossCents = sellerCents + rsgCents;
  const orderAmount = grossCents / 100;
  const commission = rsgCents / 100;
  const commPercent = grossCents > 0 ? Math.round((rsgCents / grossCents) * 10000) / 100 : 0;

  const buyerName = item.account?.name ?? item.inventoryTransaction?.account?.name ?? '';
  const sellerName = item.sellerAccount?.name ?? '';
  const productAnimal =
    item.animal?.name ?? item.inventoryTransaction?.specimen?.animal?.name ?? COMMISSION_REPORT_CONSTANTS.PRODUCT_NOT_AVAILABLE;

  return {
    id: String(item.marketPlacePaymentTransactionOrderDetailId ?? ''),
    orderId: String(item.inventoryTransactionId ?? item.inventoryTransaction?.inventoryTransactionId ?? ''),
    orderDate,
    buyer: buyerName,
    sellerBreeder: sellerName,
    productAnimal,
    orderAmount,
    commPercent,
    commission,
    status: item.inventoryTransaction?.status ?? '',
  };
};

interface CommissionReportSummaryState {
  totalOrders: number;
  grossSalesAmount: number;
  totalCommissionEarned: number;
  avgCommissionPerOrder: number;
}

const DEFAULT_SUMMARY: CommissionReportSummaryState = {
  totalOrders: 0,
  grossSalesAmount: 0,
  totalCommissionEarned: 0,
  avgCommissionPerOrder: 0,
};

interface CommissionReportCardProps {
  row: CommissionReportRow;
}

const CommissionReportCard = ({ row }: CommissionReportCardProps): JSX.Element => (
  <div className="card max-width commission-report__card">
    <div className="commission-report-card">
      <div className="commission-report-card__content">
        <div className="commission-report-card__grid">
          <div className="card-content-section">
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.ORDER_ID}</label>
              <label>{row.orderId}</label>
            </div>
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.ORDER_DATE}</label>
              <label>{row.orderDate}</label>
            </div>
            <div className="right column">
              <label>{TABLE_HEADER_CONSTANTS.BUYER}</label>
              <label>{row.buyer}</label>
            </div>
          </div>
          <div className="card-content-section">
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.SELLER}</label>
              <label>{row.sellerBreeder}</label>
            </div>
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.ANIMAL}</label>
              <label>{row.productAnimal}</label>
            </div>
            <div className="right column">
              <label>{TABLE_HEADER_CONSTANTS.ORDER_AMOUNT}</label>
              <label>${row.orderAmount.toFixed(2)}</label>
            </div>
          </div>
          <div className="card-content-section">
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.COMM_PERCENT}</label>
              <label>{row.commPercent}%</label>
            </div>
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.COMMISSION}</label>
              <label>${row.commission.toFixed(2)}</label>
            </div>
            <div className="right column">
              <label>{TABLE_HEADER_CONSTANTS.STATUS}</label>
              <label>{row.status}</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

interface CommissionReportFilterFieldsProps {
  value: CommissionReportFilterState;
  onChange: (next: CommissionReportFilterState) => void;
  dateRangeHighlight?: boolean;
}

const CommissionReportFilterFields = ({
  value,
  onChange,
  dateRangeHighlight = false,
}: CommissionReportFilterFieldsProps): JSX.Element => {
  const criteria = value.entitySearchCriteria;
  const searchKind = criteria?.name ? criteria.value.kind : null;

  return (
    <>
      <div className="commission-report__filter-group commission-report__filter-group--entity-search">
        <label className="commission-report__filter-label">{LABEL_CONSTANTS.SEARCH_BY}</label>
        <div className="commission-report__search-combined">
          <FilterDropdown
            placeholder="Select"
            options={COMMISSION_ENTITY_SEARCH_OPTIONS}
            value={criteria?.name ? criteria : COMMISSION_ENTITY_SEARCH_PLACEHOLDER}
            onChange={next =>
              onChange({
                ...value,
                entitySearchCriteria: next,
                entitySearchAccount: undefined,
                entitySearchAnimal: undefined,
              })
            }
          />
          <div className="commission-report__search-combined-input">
            {!searchKind ? (
              <ValidatedInput
                disabled
                validatedStateForAutoFill={{ value: '', valid: true }}
                placeholder=""
                label=""
                setValidatedState={() => undefined}
                validate={false}
                validators={[]}
              />
            ) : null}
            {searchKind === BUYER_SEARCH || searchKind === SELLER_SEARCH ? (
              <AccountSearch
                key={`commission-account-${searchKind}`}
                account={value.entitySearchAccount}
                onChange={account => onChange({ ...value, entitySearchAccount: account, entitySearchAnimal: undefined })}
                placeholder={
                  searchKind === SELLER_SEARCH
                    ? MARKETPLACE_MY_ORDERS_LABELS.SEARCH_SELLER
                    : COMMISSION_REPORT_CONSTANTS.SEARCH_BUYER
                }
              />
            ) : null}
            {searchKind === ANIMAL_SEARCH ? (
              <AnimalSearch
                key="commission-animal"
                animalAutofill={value.entitySearchAnimal}
                onChange={animal => onChange({ ...value, entitySearchAnimal: animal, entitySearchAccount: undefined })}
                placeholder={COMMISSION_REPORT_CONSTANTS.SEARCH_ANIMAL}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className="commission-report__filter-group commission-report__filter-group--date-range">
        <label className="commission-report__filter-label">{COMMISSION_REPORT_CONSTANTS.DATE_RANGE}</label>
        <div className="commission-report__date-range-field">
          <div
            className={`commission-report__date-range-input-wrap ${
              dateRangeHighlight ? 'commission-report__date-range-input-wrap--highlight' : ''
            }`}>
            <DateRangeInput
              value={value.orderDateRange}
              onChange={orderDateRange => onChange({ ...value, orderDateRange })}
              isClearable
              maxDate={new Date()}
              rangeLimit={1}
              highlightPlaceholder={dateRangeHighlight}
            />
          </div>
          {dateRangeHighlight ? (
            <div className="validated-input-message-error">
              <AlertSVG />
              {VALIDATION_ALERT_CONSTANTS.REQUIRED_FIELD}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

const CommissionReport = (): JSX.Element => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const user = useAppSelector(state => state.user);
  const [filters, setFilters] = useState<CommissionReportFilterState>(() => getDefaultCommissionFilters());
  const [modalFilters, setModalFilters] = useState<CommissionReportFilterState>(() => getDefaultCommissionFilters());
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(true);
  const [showDateRequiredHighlight, setShowDateRequiredHighlight] = useState(false);
  const [summary, setSummary] = useState<CommissionReportSummaryState>(DEFAULT_SUMMARY);
  const [rows, setRows] = useState<CommissionReportRow[]>([]);
  const [mobileOffset, setMobileOffset] = useState(0);
  const [tableRefresh, setTableRefresh] = useState(0);

  const mobileCallNumber = useRef(0);
  const hasAllMobileResults: MutableRefObject<boolean> = useRef(false);
  const isLoadingAdditionalMobile: MutableRefObject<boolean> = useRef(false);
  const mobileOffsetRef = useRef(0);

  useEffect(() => {
    mobileOffsetRef.current = mobileOffset;
  }, [mobileOffset]);

  const isDateRangeSelected = Boolean(filters.orderDateRange.startDate && filters.orderDateRange.endDate);
  const commissionODataFilter = useMemo(() => getCommissionReportODataFilter(filters), [filters]);

  const buildCommissionReportODataParams = useCallback((): StandardParams => {
    return {
      include: COMMISSION_REPORT_ODATA_INCLUDE,
      filter: commissionODataFilter,
      sort: COMMISSION_REPORT_ODATA_DEFAULT_SORT,
    };
  }, [commissionODataFilter]);

  const fetchSummary = useCallback(() => {
    if (accountId == null) return;
    getCommissionReportSummary(StandardParamsToODataParams(buildCommissionReportODataParams()))
      .then(response => {
        const data = response.data;
        if (data) {
          setSummary({
            totalOrders: data.totalOrders ?? 0,
            grossSalesAmount: (data.grossSalesAmountCents ?? 0) / 100,
            totalCommissionEarned: (data.totalCommissionEarnedCents ?? 0) / 100,
            avgCommissionPerOrder: (data.averageCommissionPerOrder ?? 0) / 100,
          });
        }
      })
      .catch(() => {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setSummary(DEFAULT_SUMMARY);
      });
  }, [accountId, buildCommissionReportODataParams]);

  const loadMobileData = useCallback(
    async (offsetOverride?: number) => {
      if (accountId == null) return;
      if (window.innerWidth > 767) return;
      const offset = offsetOverride ?? mobileOffsetRef.current;
      if (offset > 0 && hasAllMobileResults.current) return;

      mobileCallNumber.current++;
      const callNum = mobileCallNumber.current;

      try {
        setLoading(true);
        const base = buildCommissionReportODataParams();
        const standard: StandardParams = {
          ...base,
          limit: COMMISSION_MOBILE_PAGE_SIZE,
          offset,
        };
        const { data: response } = await getMarketplacePaymentTransactionOData(StandardParamsToODataParams(standard));
        if (callNum !== mobileCallNumber.current) return;
        const data = response ?? [];
        setRows(prev =>
          offset === 0 ? data.map(mapApiRowToCommissionReportRow) : [...prev, ...data.map(mapApiRowToCommissionReportRow)],
        );
        hasAllMobileResults.current = data.length === 0 || data.length < COMMISSION_MOBILE_PAGE_SIZE;
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        if (offset === 0) {
          setRows([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [accountId, buildCommissionReportODataParams],
  );

  useEffect(() => {
    if (!accountId) return;
    const { startDate, endDate } = filters.orderDateRange;
    if ((startDate && !endDate) || (!startDate && endDate)) return;
    fetchSummary();
    setTableRefresh(previousRefresh => previousRefresh + 1);
  }, [accountId, commissionODataFilter, fetchSummary, filters.orderDateRange]);

  const loadMoreMobile = useCallback(() => {
    setMobileOffset(prev => prev + COMMISSION_MOBILE_PAGE_SIZE);
  }, []);

  const handleMobileScroll = useCallback(() => {
    if (globalThis.innerWidth > 767) return;
    const docEl = document.documentElement;
    const body = document.body;
    const scrollTop = globalThis.pageYOffset ?? docEl.scrollTop ?? body.scrollTop ?? 0;
    const scrollHeight = Math.max(docEl.scrollHeight, body.scrollHeight);
    const clientHeight = docEl.clientHeight || globalThis.innerHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distanceFromBottom <= clientHeight;
    if (atBottom && !isLoadingAdditionalMobile.current && !hasAllMobileResults.current) {
      isLoadingAdditionalMobile.current = true;
      loadMoreMobile();
    }
  }, [loadMoreMobile]);

  useEffect(() => {
    if (accountId == null) return;
    hasAllMobileResults.current = false;
    setRows([]);
    setMobileOffset(0);
    window.innerWidth <= 767 && loadMobileData(0);
  }, [accountId, tableRefresh, loadMobileData]);

  useEffect(() => {
    if (window.innerWidth > 767) return;
    if (mobileOffset === 0) return;
    loadMobileData();
  }, [mobileOffset, loadMobileData]);

  useEffect(() => {
    isLoadingAdditionalMobile.current = false;
  }, [rows]);

  useEffect(() => {
    const opts: AddEventListenerOptions = { passive: true };
    globalThis.addEventListener('scroll', handleMobileScroll, opts);
    globalThis.addEventListener('touchmove', handleMobileScroll, opts);
    return () => {
      globalThis.removeEventListener('scroll', handleMobileScroll, opts);
      globalThis.removeEventListener('touchmove', handleMobileScroll, opts);
    };
  }, [handleMobileScroll]);

  const openFilterModal = useCallback(() => {
    setModalFilters(filters);
    setIsFilterModalOpen(true);
  }, [filters]);

  const handleModalApply = useCallback(() => {
    const { startDate, endDate } = modalFilters.orderDateRange;
    if ((startDate && !endDate) || (!startDate && endDate)) {
      showToast.error(toastMessages.SELECT_DATE_RANGE);
      return;
    }
    setFilters(modalFilters);
    setIsFilterModalOpen(false);
    setShowDateRequiredHighlight(false);
  }, [modalFilters]);

  const handleModalReset = useCallback(() => {
    setModalFilters(getDefaultCommissionFilters());
  }, []);

  const handleMobileResetFilters = useCallback(() => {
    setFilters(getDefaultCommissionFilters());
    setShowDateRequiredHighlight(false);
  }, []);

  const generateCommissionReportPayload = useCallback(() => {
    const { startDate, endDate } = filters.orderDateRange;
    if (!startDate || !endDate) return {};
    const periodStart = formatDateToShortMonthDayYear(startDate);
    const periodEnd = formatDateToShortMonthDayYear(endDate);
    let searchLine: string | undefined;
    if (filters.entitySearchCriteria?.name) {
      const { kind } = filters.entitySearchCriteria.value;
      if (kind === BUYER_SEARCH && filters.entitySearchAccount?.name) {
        searchLine = `${TABLE_HEADER_CONSTANTS.BUYER}: ${filters.entitySearchAccount.name}`;
      } else if (kind === SELLER_SEARCH && filters.entitySearchAccount?.name) {
        searchLine = `${TABLE_HEADER_CONSTANTS.SELLER}: ${filters.entitySearchAccount.name}`;
      } else if (kind === ANIMAL_SEARCH && filters.entitySearchAnimal?.name) {
        searchLine = `${ANIMAL_CONSTANTS.ANIMAL}: ${filters.entitySearchAnimal.name}`;
      }
    }
    return {
      'Created By :': `${user.firstName} ${user.lastName}`,
      'User Name :': user.username,
      'Report Period :': `${periodStart} - ${periodEnd}`,
      ...(searchLine ? { 'Search :': searchLine } : {}),
    };
  }, [filters, user.firstName, user.lastName, user.username]);

  const setCommissionReportFileName = useCallback(
    (link: HTMLAnchorElement) => {
      const { startDate, endDate } = filters.orderDateRange;
      if (!startDate || !endDate) return;
      const periodStart = formatDateToShortMonthDayYear(startDate);
      const periodEnd = formatDateToShortMonthDayYear(endDate);
      const stamp = dateFormatMMDDYYYY(new Date()).replaceAll('/', '-');
      link.setAttribute('download', `CommissionReport_${periodStart}_${periodEnd}_${stamp}.xlsx`);
    },
    [filters.orderDateRange],
  );

  const handleDownloadReport = useCallback(async () => {
    if (!downloadingReport || accountId == null) return;
    const { startDate, endDate } = filters.orderDateRange;
    if (!startDate || !endDate) return;

    try {
      setDownloadingReport(false);
      const response = await downloadCommissionReport(
        generateCommissionReportPayload(),
        StandardParamsToODataParams({
          include: COMMISSION_REPORT_ODATA_INCLUDE,
          filter: commissionODataFilter,
          sort: COMMISSION_REPORT_ODATA_DEFAULT_SORT,
        }),
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = globalThis.window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      setCommissionReportFileName(link);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.window.URL.revokeObjectURL(url);
    } catch {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setDownloadingReport(true);
    }
  }, [
    accountId,
    commissionODataFilter,
    downloadingReport,
    filters.orderDateRange,
    generateCommissionReportPayload,
    setCommissionReportFileName,
  ]);

  const handleDownloadClick = useCallback(() => {
    if (!isDateRangeSelected) {
      setShowDateRequiredHighlight(true);
      if (globalThis.window.innerWidth <= 767) {
        setModalFilters(filters);
        setIsFilterModalOpen(true);
      }
    } else {
      void handleDownloadReport();
    }
  }, [filters, handleDownloadReport, isDateRangeSelected]);

  return (
    <div className="commission-report">
      {!downloadingReport && <Loader loaderSize="small" simple appLoader />}
      <div className="commission-report__panel">
        <div className="commission-report__header">
          <div className="commission-report__header-text">
            <h1 className="commission-report__title">{PAGE_HEADER_CONSTANTS.COMMISSION_REPORT}</h1>
            <p className="commission-report__subtitle">{COMMISSION_REPORT_CONSTANTS.COMMISSION_DETAILS}</p>
          </div>
          <button
            type="button"
            className={`button outlined download-report commission-report__download ${!isDateRangeSelected ? 'disabled' : ''}`}
            onClick={handleDownloadClick}>
            <DownloadSVG />
            &nbsp;{LABEL_CONSTANTS.DOWNLOAD}
          </button>
        </div>

        <div className="commission-report__filters commission-report__filters--desktop">
          <CommissionReportFilterFields
            value={filters}
            onChange={next => {
              setShowDateRequiredHighlight(false);
              setFilters(next);
            }}
            dateRangeHighlight={showDateRequiredHighlight}
          />
        </div>

        <div className="commission-report__filter-mobile-bar">
          <button type="button" className="commission-report__filter-by-btn" onClick={openFilterModal}>
            <FilterSVG />
            <span>{LABEL_CONSTANTS.FILTER_BY}</span>
          </button>
          <button type="button" className="commission-report__reset-filters-btn" onClick={handleMobileResetFilters}>
            <ClearFilterSVG />
            <span>{LABEL_CONSTANTS.RESET_FILTERS}</span>
          </button>
        </div>

        <div className="commission-report__content">
          <div className="commission-report__summary">
            <p className="commission-report__summary-text">
              {COMMISSION_REPORT_CONSTANTS.SHOWING_ORDERS_FORMAT.replace('{count}', String(summary.totalOrders))}
            </p>
            <div className="commission-report__summary-cards">
              <ReportSummaryCard title={COMMISSION_REPORT_CONSTANTS.TOTAL_ORDERS} value={summary.totalOrders} />
              <ReportSummaryCard
                title={COMMISSION_REPORT_CONSTANTS.GROSS_SALES_AMOUNT}
                value={`$${summary.grossSalesAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              />
              <ReportSummaryCard
                title={COMMISSION_REPORT_CONSTANTS.TOTAL_COMMISSION_EARNED}
                value={`$${summary.totalCommissionEarned.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                valueHighlight
              />
              <ReportSummaryCard
                title={COMMISSION_REPORT_CONSTANTS.AVG_COMMISSION_PER_ORDER}
                value={`$${summary.avgCommissionPerOrder.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
              />
            </div>
          </div>

          <div className="desk-commission-report">
            {accountId != null && (
              <PagedTable
                headers={COMMISSION_PAGED_HEADERS}
                getData={params => getMarketplacePaymentTransactionOData(StandardParamsToODataParams(params))}
                buildRow={(item: MarketPlacePaymentTransactionOrderDetail) => {
                  const row = mapApiRowToCommissionReportRow(item);
                  return [
                    row.orderId,
                    row.orderDate,
                    row.buyer,
                    row.sellerBreeder,
                    row.productAnimal,
                    `$${row.orderAmount.toFixed(2)}`,
                    `${row.commPercent}%`,
                    `$${row.commission.toFixed(2)}`,
                    row.status,
                  ];
                }}
                include={COMMISSION_REPORT_ODATA_INCLUDE}
                filter={commissionODataFilter}
                height={500}
                minHeight={156}
                pageSize={10}
                scrollBufferThickness={100}
                sortBy="createdDatetime"
                sortDescByDefault
                refresh={tableRefresh}
                setRefresh={setTableRefresh}
                noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
              />
            )}
          </div>

          <div className="xs-commission-report">
            {!loading && rows.length === 0 ? (
              <div className="commission-report__no-results">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
            ) : (
              rows.length > 0 && rows.map(row => <CommissionReportCard key={row.id} row={row} />)
            )}
            {loading && <Loader addedSpace loaderSize="small" />}
          </div>
        </div>
      </div>

      {isFilterModalOpen && (
        <div className="commission-report__filter-modal-wrap">
          <Modal isOpen={isFilterModalOpen} onClose={() => setIsFilterModalOpen(false)} ignoreBackdrop>
            <div className="commission-report__filter-modal">
              <h2 className="commission-report__filter-modal-title">{LABEL_CONSTANTS.FILTER_BY}</h2>
              <button type="button" className="commission-report__filter-modal-reset" onClick={handleModalReset}>
                <ClearFilterSVG />
                <span>{LABEL_CONSTANTS.RESET_FILTERS}</span>
              </button>
              <div className="commission-report__filter-modal-body">
                <CommissionReportFilterFields
                  value={modalFilters}
                  onChange={next => {
                    setShowDateRequiredHighlight(false);
                    setModalFilters(next);
                  }}
                  dateRangeHighlight={showDateRequiredHighlight}
                />
              </div>
              <div className="commission-report__filter-modal-footer">
                <button type="button" className="button inverted medium green" onClick={() => setIsFilterModalOpen(false)}>
                  {BUTTON_CONSTANTS.CANCEL}
                </button>
                <button type="button" className="button medium green" onClick={handleModalApply}>
                  {BUTTON_CONSTANTS.APPLY}
                </button>
              </div>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default CommissionReport;
